import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Navigation from "@/components/navigation";
import PropertyMap from "@/components/property-map";
import PropertyDetailModal from "@/components/property-detail-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, Filter, X, MapPin, Home, ChevronDown, Search } from "lucide-react";
import type { Property } from "@shared/schema";
import { useViewport } from "@/hooks/useViewport";
import MobileSearchScreen from "@/features/mobile/MobileSearchScreen";

export default function MapSearch() {
  const { isMobile } = useViewport();
  const [location] = useLocation();
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  // Using demo property data - integrates with your IDX feed for live listings
  const [showFilters, setShowFilters] = useState(false);
  const [isPropertyDetailModalOpen, setIsPropertyDetailModalOpen] = useState(false);
  const [propertyDetailData, setPropertyDetailData] = useState<Property | null>(null);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [leadFormProperty, setLeadFormProperty] = useState<Property | null>(null);
  const [leadType, setLeadType] = useState<'tour' | 'contact'>('tour');
  const [filters, setFilters] = useState({
    city: "",
    minPrice: "",
    maxPrice: "",
    bedrooms: "",
    bathrooms: "",
    propertyType: "",
  });
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const [targetBounds, setTargetBounds] = useState<{
    north: number; south: number; east: number; west: number;
  } | null>(null);
  const [locationQuery, setLocationQuery] = useState<string>("");
  const didInitFromCityRef = useRef<boolean>(false);

  // Initialize filters from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.split('?')[1] || '');
    setFilters({
      city: searchParams.get('city') || 'Miami Beach, FL',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      bedrooms: searchParams.get('bedrooms') || '',
      bathrooms: searchParams.get('bathrooms') || '',
      propertyType: searchParams.get('propertyType') || '',
    });
    setLocationQuery(searchParams.get('city') || 'Miami Beach, FL');
    // If bounds were provided (from landing page autocomplete), apply immediately
    const boundsParam = searchParams.get('bounds');
    if (boundsParam) {
      try {
        const b = JSON.parse(boundsParam);
        if (b && [b.north, b.south, b.east, b.west].every((n: any) => Number.isFinite(n))) {
          setTargetBounds(b);
          setMapBounds(b);
        }
      } catch {}
    }
  }, [location]);

  const [mapBounds, setMapBounds] = useState<{
    north: number;
    south: number; 
    east: number;
    west: number;
  } | null>({
    // Initial Miami Beach viewport to speed up first load
    north: 25.85,
    south: 25.74,
    east: -80.10,
    west: -80.17,
  });

  const [drawnBounds, setDrawnBounds] = useState<{
    north: number;
    south: number; 
    east: number;
    west: number;
  } | null>(null);

  // Cache all properties on frontend - RELIABLE CACHING
  const { data: allProperties = [], isLoading, refetch, error } = useQuery<Property[]>({
    queryKey: ["/api/properties", "waterfront", mapBounds],
    queryFn: async () => {
      console.log('🔄 Loading properties from server...');
      const params = new URLSearchParams();
      params.set('isWaterfront', 'true'); // Only waterfront properties
      params.set('lean', 'true'); // Lighter payload for map
      params.set('limit', '800'); // cap
      if (mapBounds) {
        params.set('bounds', JSON.stringify(mapBounds));
        console.log('🗺️ Fetching properties for viewport bounds:', mapBounds);
      }
      const response = await fetch(`/api/properties?${params.toString()}`);
      if (!response.ok) {
        // More detailed error handling
        const errorText = await response.text().catch(() => response.statusText);
        console.error(`❌ API Error: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch properties: ${response.statusText}`);
      }
      let data = await response.json();
      console.log(`📊 Loaded ${data.length} waterfront properties${mapBounds ? ' in current viewport' : ' total'}`);
      // Fallback: if none found in a tight viewport, expand to South Florida region once
      if (Array.isArray(data) && data.length === 0 && mapBounds) {
        const fallbackBounds = { north: 27.5, south: 24.3, east: -79.8, west: -82.0 };
        const fp = new URLSearchParams();
        fp.set('isWaterfront', 'true');
        fp.set('lean', 'true');
        fp.set('limit', '800');
        fp.set('bounds', JSON.stringify(fallbackBounds));
        console.log('🛟 No results in viewport, trying fallback South Florida bounds...');
        const r2 = await fetch(`/api/properties?${fp.toString()}`);
        if (r2.ok) {
          const d2 = await r2.json();
          console.log(`✅ Fallback loaded ${d2.length} properties in South Florida region`);
          data = d2;
        }
      }
      return data;
    },
    staleTime: 30000,     // Cache for 30 seconds (shorter for bounds changes)
    cacheTime: 300000,    // Keep in cache for 5 minutes
    retry: 3,             // Retry more times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false, // Disable auto-refresh to prevent errors
  });

  // SMART filtering - Show properties based on map location AND user filters
  const properties = React.useMemo(() => {
    let filtered = allProperties;
    
    // Apply bounds filtering ONLY when user draws a custom area
    const boundsToUse = drawnBounds; // Do not auto-filter by map view to avoid sudden disappearance
    if (boundsToUse) {
      filtered = filtered.filter(property => {
        const lat = typeof property.latitude === 'string' ? parseFloat(property.latitude) : property.latitude;
        const lng = typeof property.longitude === 'string' ? parseFloat(property.longitude) : property.longitude;
        
        return lat && lng &&
          lat <= boundsToUse.north &&
          lat >= boundsToUse.south &&
          lng <= boundsToUse.east &&
          lng >= boundsToUse.west;
      });
    }
    
    // Apply user-selected filters
    if (filters.minPrice) {
      const minPrice = parseInt(filters.minPrice);
      filtered = filtered.filter(p => (p.price || 0) >= minPrice);
    }
    if (filters.maxPrice) {
      const maxPrice = parseInt(filters.maxPrice);
      filtered = filtered.filter(p => (p.price || 0) <= maxPrice);
    }
    if (filters.bedrooms) {
      const bedrooms = parseInt(filters.bedrooms);
      filtered = filtered.filter(p => (p.bedrooms || 0) >= bedrooms);
    }
    if (filters.bathrooms) {
      const bathrooms = parseInt(filters.bathrooms);
      filtered = filtered.filter(p => (p.bathrooms || 0) >= bathrooms);
    }
    
    // Determine the area being viewed for logging
    const currentArea = drawnBounds ? "drawn area" : 
                       mapBounds ? "current map view" : "all South Florida";
    
    console.log(`🎯 Showing ${filtered.length} properties in ${currentArea}`);
    return filtered;
  }, [allProperties, filters, drawnBounds, mapBounds]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Geocode a location string to bounds using Nominatim (OpenStreetMap)
  const geocodeLocationToBounds = async (query: string): Promise<{ north: number; south: number; east: number; west: number; } | null> => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1&addressdetails=0&polygon_geojson=0&extratags=0`;
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!resp.ok) return null;
      const data = await resp.json();
      if (!Array.isArray(data) || data.length === 0) return null;
      const first = data[0];
      if (first && first.boundingbox) {
        const [south, north, west, east] = first.boundingbox.map((v: string) => parseFloat(v));
        if ([south, north, west, east].every(n => Number.isFinite(n))) {
          return { north, south, east, west };
        }
      }
      // Fallback to point + small box
      const lat = parseFloat(first.lat);
      const lon = parseFloat(first.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        const dLat = 0.05, dLon = 0.05;
        return { north: lat + dLat, south: lat - dLat, east: lon + dLon, west: lon - dLon };
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleLocationSearch = async () => {
    const q = locationQuery.trim();
    if (!q) return;
    const bounds = await geocodeLocationToBounds(q);
    if (bounds) {
      setTargetBounds(bounds);
      setMapBounds(bounds);
      setDrawnBounds(null);
      setFilters(prev => ({ ...prev, city: q }));
      await refetch();
    }
  };

  const handlePropertySelect = (property: Property) => {
    setSelectedProperty(property);
    // Auto-open the property detail modal when a marker is clicked
    setPropertyDetailData(property);
    setIsPropertyDetailModalOpen(true);
  };

  const handleScheduleTour = (property: Property) => {
    setLeadFormProperty(property);
    setLeadType('tour');
    setIsLeadFormOpen(true);
    setIsPropertyDetailModalOpen(true);
  };

  const handleContactAgent = (property: Property) => {
    setLeadFormProperty(property);
    setLeadType('contact');
    setIsLeadFormOpen(true);
    setIsPropertyDetailModalOpen(true);
  };



  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return () => {};
  }, []);

  // Debounce map bounds changes to avoid excessive API calls
  const handleMapBoundsChange = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout;
      return (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
    setMapBounds(bounds);
        }, 300); // 300ms debounce
  };
    }, []),
    []
  );

  const handleDrawnArea = (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null) => {
    setDrawnBounds(bounds);
    // Clear location filter when using drawn area
    if (bounds) {
      setFilters(prev => ({ ...prev, city: "" }));
    }
  };

  // Auto-apply location from landing page (city query param) by geocoding once on load
  useEffect(() => {
    const init = async () => {
      if (didInitFromCityRef.current) return;
      if (!locationQuery || drawnBounds) return;
      const bounds = await geocodeLocationToBounds(locationQuery);
      if (bounds) {
        setTargetBounds(bounds);
        setMapBounds(bounds);
        await refetch();
      }
      didInitFromCityRef.current = true;
    };
    void init();
  }, [locationQuery, drawnBounds, refetch]);

  const clearFilters = () => {
    setFilters({
      city: "Miami Beach, FL",
      minPrice: "",
      maxPrice: "",
      bedrooms: "",
      bathrooms: "",
      propertyType: "",
    });
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (!numPrice) return 'Price on request';
    if (numPrice >= 1000000) {
      return `$${(numPrice / 1000000).toFixed(1)}M`;
    } else if (numPrice >= 1000) {
      return `$${(numPrice / 1000).toFixed(0)}K`;
    }
    return `$${numPrice.toLocaleString()}`;
  };

  if (isMobile) {
    return (
      <>
        <MobileSearchScreen
          properties={properties}
          selectedPropertyId={selectedProperty?.id ?? null}
          onBoundsChanged={(bounds) => {
            setMapBounds(bounds);
            void refetch();
          }}
          onSaveSearch={() => {
            /* hook up when save search implemented */
          }}
          onPropertySelect={(property) => {
            setSelectedProperty(property);
            setPropertyDetailData(property);
            setIsPropertyDetailModalOpen(true);
          }}
          onScheduleTour={handleScheduleTour}
          onContactAgent={handleContactAgent}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          isModalOpen={isPropertyDetailModalOpen}
        />
        <PropertyDetailModal
          isOpen={isPropertyDetailModalOpen}
          onClose={() => {
            if (!isLeadFormOpen) {
              setIsPropertyDetailModalOpen(false);
              setPropertyDetailData(null);
            }
          }}
          property={propertyDetailData}
          onScheduleTour={handleScheduleTour}
          onContactAgent={handleContactAgent}
          isLeadFormOpen={isLeadFormOpen}
          leadType={leadType}
          onCloseLeadForm={() => setIsLeadFormOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Header Section */}
      <div className="bg-gradient-to-r from-ocean-blue to-bright-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-playfair text-4xl font-bold mb-2">
                South FL Waterfront Properties
              </h1>
              <p className="text-xl opacity-90">
                Discover luxury waterfront homes with our interactive map
              </p>
            </div>
            
            {/* Search Status */}
            <div className="flex items-center gap-3">
              {drawnBounds ? (
                <Badge className="bg-green-600 text-white">
                  Custom Area Search
                </Badge>
              ) : (
                <Badge className="bg-luxury-gold text-deep-navy">
                  Live Data
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Search Bar */}
      <div className="bg-gray-50 border-b py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder={drawnBounds ? "Location search disabled (using drawn area)" : "Search location (e.g., Miami Beach, Key Biscayne, Fort Lauderdale)"}
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                disabled={!!drawnBounds}
                className={`h-12 text-lg ${drawnBounds ? 'bg-gray-100 text-gray-500' : ''}`}
                onKeyDown={(e) => { if (e.key === 'Enter' && !drawnBounds) handleLocationSearch(); }}
              />
              <Button 
                onClick={handleLocationSearch}
                disabled={!!drawnBounds}
                className="h-12 px-4 bg-ocean-blue hover:bg-bright-blue text-white"
                title="Search"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="relative" ref={filterDropdownRef}>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 px-6"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {Object.values(filters).filter(v => v && v !== "").length > 0 && (
                <Badge className="ml-2 bg-ocean-blue">
                  {Object.values(filters).filter(v => v && v !== "").length}
                </Badge>
              )}
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            
              {/* Dropdown Filters */}
          {showFilters && (
                <>
                  {/* Backdrop */}
                  <div className="fixed inset-0 z-[9998]" onClick={() => setShowFilters(false)} />
                  {/* Dropdown */}
                  <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-lg shadow-lg border z-[9999] p-4">
                  <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Min Price</Label>
                  <Input
                    placeholder="$500,000"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Max Price</Label>
                  <Input
                    placeholder="$10,000,000"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Bedrooms</Label>
                      <select 
                        value={filters.bedrooms} 
                        onChange={(e) => handleFilterChange("bedrooms", e.target.value)}
                        className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm"
                      >
                        <option value="">Any</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                        <option value="4">4+</option>
                        <option value="5">5+</option>
                      </select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Property Type</Label>
                      <select 
                        value={filters.propertyType} 
                        onChange={(e) => handleFilterChange("propertyType", e.target.value)}
                        className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm"
                      >
                        <option value="">All Types</option>
                        <option value="Single Family">House</option>
                        <option value="Condo">Condo</option>
                        <option value="Estate">Estate</option>
                      </select>
                </div>
                    <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={clearFilters}
                        className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                      <Button 
                        onClick={() => setShowFilters(false)}
                        className="flex-1 bg-ocean-blue hover:bg-bright-blue text-white"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
                </>
              )}
            </div>
            
            <Button 
              onClick={() => refetch()}
              className="h-12 px-6 bg-ocean-blue hover:bg-bright-blue"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content: Map + Properties */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-6 h-[calc(100vh-280px)]">
          <div className="lg:col-span-3 overflow-y-auto pr-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-playfair text-2xl font-bold text-deep-navy">
                {isLoading
                  ? "Loading..."
                  : error
                    ? "Search Error"
                    : `${properties.length} Properties Found`}
              </h2>
              {drawnBounds && (
                <div className="text-sm text-gray-600 bg-green-50 px-3 py-1 rounded-full border border-green-200">
                  Custom Area Selected
                </div>
              )}
            </div>

            {error && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="text-yellow-600">⚠️</div>
                  <div className="flex-1">
                    <p className="text-yellow-800 text-sm font-medium mb-1">
                      Connection Issue
                    </p>
                    <p className="text-yellow-700 text-sm">
                      Unable to refresh latest properties. Showing cached results for now.
                    </p>
                    <button
                      onClick={() => refetch()}
                      className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm underline"
                    >
                      Try refreshing
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : (
          <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
            {properties.slice(0, 100).map((property, index) => (
              <div
                key={`${property.id}-${index}`}
                className={`cursor-pointer transition-all duration-200 ${
                  selectedProperty?.id === property.id ? 'ring-2 ring-ocean-blue' : ''
                }`}
                onClick={() => handlePropertySelect(property)}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div
                          className="w-32 h-24 bg-cover bg-center rounded-lg"
                          style={{
                            backgroundImage: `url('${property.images?.[0] || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop"}')`
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-lg text-deep-navy truncate">
                            {formatPrice(property.price)}
                          </h3>
                          {property.propertyType && (
                            <Badge variant="secondary" className="text-xs">
                              {property.propertyType}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          {property.bedrooms && <span>{property.bedrooms} bed</span>}
                          {property.bathrooms && <span>{property.bathrooms} bath</span>}
                          {property.squareFeet && <span>{property.squareFeet.toLocaleString()} ft²</span>}
                        </div>
                        <div className="flex items-start gap-1 text-sm text-gray-500">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span className="truncate">
                            {property.address || `${property.city}, ${property.state}`}
                          </span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScheduleTour(property);
                            }}
                            className="bg-ocean-blue hover:bg-bright-blue text-white"
                          >
                            Schedule Tour
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPropertyDetailData(property);
                              setIsPropertyDetailModalOpen(true);
                            }}
                            className="border-ocean-blue text-ocean-blue hover:bg-ocean-blue hover:text-white"
                          >
                            <Home className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
            {properties.length > 100 && (
              <div className="text-center py-4 text-gray-500">
                Showing first 100 of {properties.length} properties. Use map bounds to filter.
              </div>
            )}
          </div>
            )}

            {properties.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No Properties Found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search criteria or location.
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="h-full rounded-lg overflow-hidden shadow-lg">
              <PropertyMap
                properties={properties}
                selectedPropertyId={selectedProperty?.id}
                onPropertySelect={handlePropertySelect}
                onBoundsChange={handleMapBoundsChange}
                onDrawnArea={handleDrawnArea}
                height="100%"
                className="h-full"
                targetBounds={targetBounds}
              />
            </div>
          </div>
        </div>
      </div>


      {/* Property Detail Modal */}
      <PropertyDetailModal
        isOpen={isPropertyDetailModalOpen}
        onClose={() => {
          // Only close if the lead form is not open
          if (!isLeadFormOpen) {
            setIsPropertyDetailModalOpen(false);
            setPropertyDetailData(null);
          }
        }}
        property={propertyDetailData}
        onScheduleTour={handleScheduleTour}
        onContactAgent={handleContactAgent}
        isLeadFormOpen={isLeadFormOpen}
        leadType={leadType}
        onCloseLeadForm={() => setIsLeadFormOpen(false)}
      />

    </div>
  );
}